(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  const setupMagnet = (root) => {
    root.querySelectorAll('[data-dolo-magnet]').forEach((button) => {
      if (button.dataset.doloMagnetReady === 'true') return;
      button.dataset.doloMagnetReady = 'true';

      button.addEventListener('pointermove', (event) => {
        if (!window.anime || prefersReduced.matches) return;
        const rect = button.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 12;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 12;
        window.anime.remove(button);
        window.anime({ targets: button, translateX: x, translateY: y, duration: 180, easing: 'easeOutQuad' });
      });

      button.addEventListener('pointerleave', () => {
        if (!window.anime || prefersReduced.matches) return;
        window.anime.remove(button);
        window.anime({ targets: button, translateX: 0, translateY: 0, duration: 520, easing: 'easeOutElastic(1, .45)' });
      });
    });
  };

  const setupReleasePopup = (root) => {
    const popup = root.querySelector('[data-dolo-release-popup]');
    if (!popup || popup.dataset.doloPopupReady === 'true') return;

    popup.dataset.doloPopupReady = 'true';
    const dialog = popup.querySelector('[role="dialog"]');
    const closeButtons = popup.querySelectorAll('[data-dolo-popup-close]');
    const storageKey = 'dolo-fall-in-love-popup-dismissed';
    let previousFocus = null;

    const wasDismissed = () => {
      if (window.Shopify && window.Shopify.designMode) return false;
      try {
        return window.sessionStorage.getItem(storageKey) === 'true';
      } catch (error) {
        return false;
      }
    };

    const rememberDismissal = () => {
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {
        // The popup can still close when storage is unavailable.
      }
    };

    const closePopup = () => {
      rememberDismissal();
      document.body.classList.remove('dolo-popup-open');

      const finish = () => {
        popup.hidden = true;
        popup.style.opacity = '';
        dialog.style.transform = '';
        if (previousFocus) previousFocus.focus({ preventScroll: true });
      };

      if (!window.anime || prefersReduced.matches) {
        finish();
        return;
      }

      window.anime({
        targets: popup,
        opacity: [1, 0],
        duration: 220,
        easing: 'easeOutQuad',
        complete: finish,
      });
    };

    const openPopup = () => {
      if (wasDismissed()) return;
      previousFocus = document.activeElement;
      popup.hidden = false;
      document.body.classList.add('dolo-popup-open');

      if (window.anime && !prefersReduced.matches) {
        window.anime({
          targets: dialog,
          translateY: [24, 0],
          rotate: [-0.8, 0],
          opacity: [0, 1],
          duration: 620,
          easing: 'easeOutExpo',
        });
      }

      window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
    };

    closeButtons.forEach((button) => button.addEventListener('click', closePopup));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hidden) closePopup();
    });

    window.setTimeout(openPopup, 700);
  };

  const setupScrollPanels = (root) => {
    const panels = root.querySelectorAll('[data-dolo-panel], [data-dolo-product]');
    if (!panels.length) return;

    if (!('IntersectionObserver' in window) || prefersReduced.matches || !window.anime || window.matchMedia('(max-width: 989px)').matches) {
      panels.forEach((panel) => {
        panel.style.opacity = 1;
        panel.style.transform = 'none';
      });
      return;
    }

    panels.forEach((panel) => {
      if (panel.dataset.doloObserved === 'true') return;
      panel.dataset.doloObserved = 'true';
      panel.style.opacity = 1;
      panel.style.transform = 'translateY(16px) rotate(.18deg)';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          window.anime({
            targets: entry.target,
            translateY: [16, 0],
            rotate: [0.18, 0],
            duration: 650,
            easing: 'easeOutExpo',
          });
        });
      },
      { threshold: 0.04, rootMargin: '0px 0px 18% 0px' }
    );

    panels.forEach((panel) => observer.observe(panel));
  };

  const setupParallax = (root) => {
    const snapshot = root.querySelector('[data-dolo-parallax]');
    if (!snapshot || prefersReduced.matches) return;

    let ticking = false;
    const update = () => {
      const progress = Math.min(window.scrollY / Math.max(window.innerHeight, 1), 1);
      snapshot.style.setProperty('--dolo-parallax', progress.toFixed(3));
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  const setupBlobScroll = (root) => {
    const blobs = [...root.querySelectorAll('[data-dolo-blob]')];
    if (!blobs.length || prefersReduced.matches) return;

    let ticking = false;
    const update = () => {
      const progress = Math.min(Math.max(window.scrollY / Math.max(window.innerHeight, 1), 0), 3.2);

      blobs.forEach((blob, index) => {
        const direction = index % 2 === 0 ? 1 : -1;
        const reach = 18 + index * 8;
        const x = Math.sin(progress * 2.4 + index * 0.9) * reach + progress * direction * (8 + index * 2);
        const y = Math.cos(progress * 2 + index * 0.7) * (12 + index * 5) - progress * (7 + index * 2);
        const rotate = direction * (progress * (10 + index * 5));

        blob.style.setProperty('--dolo-drift-x', `${x.toFixed(1)}px`);
        blob.style.setProperty('--dolo-drift-y', `${y.toFixed(1)}px`);
        blob.style.setProperty('--dolo-drift-rotate', `${rotate.toFixed(1)}deg`);
      });

      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  };

  const startBlobLoops = (root) => {
    if (!window.anime || prefersReduced.matches) return;

    window.anime({
      targets: root.querySelector('[data-dolo-hero-logo]'),
      translateY: [0, -8],
      duration: 3000,
      direction: 'alternate',
      easing: 'easeInOutSine',
      loop: true,
    });

    window.anime({
      targets: root.querySelector('.dolo-hero__noise'),
      opacity: [0.7, 1],
      duration: 2200,
      direction: 'alternate',
      easing: 'easeInOutSine',
      loop: true,
    });
  };

  const runIntroReveal = (root) => {
    startBlobLoops(root);
  };

  const initDoloHome = (scope = document) => {
    scope.querySelectorAll('[data-dolo-home]').forEach((root) => {
      if (root.dataset.doloReady === 'true') return;
      root.dataset.doloReady = 'true';

      setupMagnet(root);
      setupReleasePopup(root);
      setupScrollPanels(root);
      setupParallax(root);
      setupBlobScroll(root);

      const run = () => {
        runIntroReveal(root);
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    });
  };

  document.addEventListener('shopify:section:load', (event) => initDoloHome(event.target));
  initDoloHome();
})();
